import React, { useEffect, useRef } from 'react';

interface DayHours {
  day: string;
  time: string;
  hours: string;
}

interface BusinessHoursDialogProps {
  isOpen: boolean;
  onClose: () => void;
  businessHours: DayHours[];
  onChange: (updatedHours: DayHours[]) => void;
}

const BusinessHoursDialog: React.FC<BusinessHoursDialogProps> = ({ isOpen, onClose, businessHours, onChange }) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Helper function to parse time string like "7:00 AM TO 9:00 PM" into start and end times
  const parseTimeString = (timeString: string) => {
    const parts = timeString.split(' TO ');
    if (parts.length !== 2) return { start: '', end: '' };
    
    const convertTo24Hour = (time: string) => {
      const [timePart, period] = time.split(' ');
      const [hours, minutes] = timePart.split(':');
      let hour24 = parseInt(hours);
      
      if (period === 'PM' && hour24 !== 12) {
        hour24 += 12;
      } else if (period === 'AM' && hour24 === 12) {
        hour24 = 0;
      }
      
      return `${hour24.toString().padStart(2, '0')}:${minutes}`;
    };
    
    return {
      start: convertTo24Hour(parts[0]),
      end: convertTo24Hour(parts[1])
    };
  };

  // Helper function to convert 24-hour time back to 12-hour format with AM/PM
  const convertTo12Hour = (time24: string) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${period}`;
  };

  // Helper function to calculate hours between two times
  const calculateHours = (startTime: string, endTime: string) => {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    
    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    
    const diffMinutes = endTotalMinutes - startTotalMinutes;
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    
    if (minutes === 0) {
      return `${hours} hours`;
    } else {
      return `${hours} hours ${minutes} minutes`;
    }
  };

  // Handle time changes
  const handleTimeChange = (dayIndex: number, field: 'start' | 'end', value: string) => {
    const updatedHours = [...businessHours];
    const currentDay = updatedHours[dayIndex];
    const currentTimes = parseTimeString(currentDay.time);
    
    const newTimes = {
      ...currentTimes,
      [field]: value
    };
    
    if (newTimes.start && newTimes.end) {
      const startTime12 = convertTo12Hour(newTimes.start);
      const endTime12 = convertTo12Hour(newTimes.end);
      const newTimeString = `${startTime12} TO ${endTime12}`;
      const newHours = calculateHours(newTimes.start, newTimes.end);
      
      updatedHours[dayIndex] = {
        ...currentDay,
        time: newTimeString,
        hours: newHours
      };
      
      onChange(updatedHours);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div ref={dialogRef} className="bg-white p-8 rounded-lg shadow-lg w-full max-w-3xl overflow-y-auto max-h-[80vh]">
        <h2 className="text-2xl font-bold mb-6 text-center">Edit Business Hours</h2>
        <div className="space-y-4">
          {businessHours.map((dayData, index) => {
            const { start, end } = parseTimeString(dayData.time);
            return (
              <div key={dayData.day} className="flex flex-col mb-4">
                <label className="block text-lg font-semibold mb-2">{dayData.day}</label>
                <div className="flex space-x-2">
                  <input
                    type="time"
                    value={start}
                    onChange={(e) => handleTimeChange(index, 'start', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg text-lg"
                  />
                  <input
                    type="time"
                    value={end}
                    onChange={(e) => handleTimeChange(index, 'end', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg text-lg"
                  />
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {dayData.time} ({dayData.hours})
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end mt-6">
          <button onClick={onClose} className="bg-brutal-dark text-white px-6 py-3 rounded-lg text-lg hover:bg-brutal-charcoal transition">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessHoursDialog;